import { GoogleMap, GoogleMapProps, MarkerF } from '@react-google-maps/api'
import { useEffect, useMemo, useState } from 'react'

import { GeoData } from 'src/types/map'

export interface MapProps {
  geoData: GeoData
  onPin?: (geoLocation: google.maps.LatLngLiteral) => void
  zoom: number
  constrainMap?: boolean
  readOnly?: boolean
}

export const DEFAULT_CENTER = { lat: -41.2865, lng: 174.7762 }

const NEW_ZEALAND_BOUNDS: google.maps.LatLngBoundsLiteral = {
  north: -34.36,
  south: -47.35,
  west: 166.28,
  east: -175.81
}

const DEFAULT_MAP_PROPS: GoogleMapProps = {
  mapContainerStyle: {
    width: '100%',
    height: '400px'
  },
  options: {
    mapTypeControl: true,
    streetViewControl: false,
    mapTypeId: 'satellite'
  }
}

export const Map = ({
  geoData,
  onPin,
  zoom,
  constrainMap,
  readOnly
}: MapProps) => {
  const [center, setCenter] = useState<google.maps.LatLngLiteral>(
    geoData.place_id ? { lat: geoData.lat, lng: geoData.lng } : DEFAULT_CENTER
  )
  const [markerPosition, setMarkerPosition] =
    useState<google.maps.LatLngLiteral | null>({
      lat: geoData.lat,
      lng: geoData.lng
    })

  const finalMapProps = useMemo<GoogleMapProps>(() => {
    if (constrainMap) {
      return {
        ...DEFAULT_MAP_PROPS,
        options: {
          ...DEFAULT_MAP_PROPS.options,
          minZoom: 5,
          maxZoom: 15,
          restriction: {
            latLngBounds: NEW_ZEALAND_BOUNDS
          }
        }
      }
    }
    return DEFAULT_MAP_PROPS
  }, [constrainMap])

  useEffect(() => {
    setMarkerPosition(geoData)

    // Delay setting center so we have a visual changes of center being moved
    setTimeout(() => {
      if (geoData.place_id) setCenter(geoData)
    }, 150)
  }, [geoData])

  const handleMapClick = ({ latLng }: google.maps.MapMouseEvent) => {
    if (readOnly) return
    if (latLng) {
      const geo = { lat: latLng.lat(), lng: latLng.lng() }
      if (onPin) onPin(geo)
      setMarkerPosition(geo)

      // Delay setting center so we have a visual changes of center being moved
      setTimeout(() => {
        setCenter(geo)
      }, 150)
    }
  }

  return (
    <GoogleMap
      {...finalMapProps}
      center={center}
      onClick={handleMapClick}
      zoom={zoom}
    >
      {geoData.place_id && markerPosition?.lat && markerPosition?.lng ? (
        <MarkerF position={markerPosition} />
      ) : null}
    </GoogleMap>
  )
}
